import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { Button, Result, Spin, Tag } from "antd";
import {
  PlannerLogProvider,
  useWorkspaceData
} from "pages/planning-workspace/hooks/use-workspace-data";
import { useWorkspaceFilters } from "pages/planning-workspace/hooks/use-workspace-filters";
import { applyFilters } from "pages/planning-workspace/adapter";
import TimelineView from "pages/planning-workspace/components/timeline-view";
import { WorkspaceDesignPackage } from "pages/planning-workspace/model";
import BlankState from "components/blank-state";
import { CiqIconBox } from "components/svg-icons";
import { ErrorMessages } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import ExportPdfPrintModal from "pages/print-view-layout/Export-Pdf-Print-Modal";
import ExportOptionsPopover, {
  ExportFormatOption
} from "components/export-options-popover";
import { savePrintViewState } from "pages/print-view-layout/print-view-storage";
import { PrintViewLayoutState } from "pages/print-view-layout/model";
import { DateUtils } from "utils/dateutils";
import { compareStringsAlphabetically } from "utils/utils";
import { DependencyModel } from "pages/design-package-details-page/components/view-dependencies/model";
import DesignPackageSidebar, { SidebarPackage } from "./package-sidebar";
import HowToUsePopover from "./how-to-use-popover";

const STATUS_LABELS: Record<string, string> = {
  WF_NOT_STARTED: "Not Started",
  NOT_STARTED: "Not Started",
  IN_PROGRESS: "In Progress",
  WF_IN_PROGRESS: "In Progress",
  WF_COMPLETED: "Completed",
  COMPLETED: "Completed",
  VOID: "Void"
};

const PLANNER_EXPORT_OPTIONS: ExportFormatOption[] = [
  {
    key: "print_view",
    title: "PDF/Print",
    testId: "planner-export-pdf"
  }
];

function milestoneStatusLabel(dp: WorkspaceDesignPackage) {
  const raw = dp.milestoneState || dp.status;
  if (!raw) return "Not Started";
  return STATUS_LABELS[raw] ?? raw;
}

function designPackageIdFromPath(pathname: string): string | undefined {
  const match = pathname.match(/\/design-packages\/([^/?#]+)/);
  if (!match || match[1] === "details") return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function toSidebarPackage(
  dp: WorkspaceDesignPackage,
  detailsById: Record<string, DependencyModel>
): SidebarPackage {
  return {
    id: dp.id,
    sequence: dp.number || "",
    title: dp.title,
    statusLabel: milestoneStatusLabel(dp),
    bidPackageCount: detailsById[dp.id]?.bidPackages?.length ?? 0
  };
}

export default function PlannerCardView(props: {
  projectId: string;
  addButton: React.ReactNode;
  refreshKey?: number;
  canAdd?: boolean;
  onAddDesignPackage?: () => void;
}) {
  const {
    projectId,
    addButton,
    refreshKey = 0,
    canAdd = true,
    onAddDesignPackage
  } = props;
  const { projectDetails, isFeatureFlagEnabled, isProjectArchived } =
    useContext(ProjectContext);
  const history = useHistory();
  const location = useLocation();
  const { designPackageId: routeDesignPackageId } = useParams<{
    designPackageId?: string;
  }>();
  const designPackageId =
    routeDesignPackageId ?? designPackageIdFromPath(location.pathname);
  const isPrintViewEnabled = isFeatureFlagEnabled("PRINT_VIEW");
  const { filters, setQuery } = useWorkspaceFilters();
  const {
    model,
    detailsById = {},
    loading,
    error,
    refetch
  } = useWorkspaceData(projectId, refreshKey);
  const filtered = applyFilters(model, filters, detailsById);
  const sortedPackages = useMemo(
    () =>
      [...filtered.designPackages].sort((a, b) =>
        compareStringsAlphabetically(a.number || "", b.number || "")
      ),
    [filtered.designPackages]
  );
  const visibleCount = filtered.designPackages.length;
  const projectIsEmpty = model.designPackages.length === 0;
  const logValue = {
    detailsById,
    loading,
    refetch
  };

  const [sidebarOpen, setSidebarOpen] = useState(true);

  const logLoaded = !loading || model.designPackages.length > 0;
  const inLog = Boolean(
    designPackageId &&
    model.designPackages.some((dp) => dp.id === designPackageId)
  );
  const firstId = sortedPackages[0]?.id ?? null;

  // A bare /design-packages link opens the first package and keeps that id
  // in the address bar so a reload or a shared link returns to it.
  useEffect(() => {
    if (designPackageId || !logLoaded || !firstId) return;
    history.replace({
      pathname: `/project/${projectId}/design-packages/${firstId}`,
      search: location.search
    });
  }, [
    designPackageId,
    logLoaded,
    firstId,
    history,
    projectId,
    location.search
  ]);

  const selectPackage = (id: string) => {
    if (id === designPackageId) return;
    history.push({
      pathname: `/project/${projectId}/design-packages/${id}`,
      search: location.search
    });
  };

  const sidebarPackages = useMemo(
    () =>
      sortedPackages.map((dp) =>
        toSidebarPackage(dp, detailsById as Record<string, DependencyModel>)
      ),
    [detailsById, sortedPackages]
  );

  const selected = inLog
    ? model.designPackages.find((dp) => dp.id === designPackageId)
    : designPackageId
      ? undefined
      : sortedPackages[0];
  const missingPackage = logLoaded && Boolean(designPackageId) && !inLog;
  const focusedModel = {
    designPackages: selected ? [selected] : []
  };
  const allTitles = model.designPackages.map((dp) => dp.title);

  const onOpenPlannerPrintView = () => {
    const printViewState: PrintViewLayoutState = {
      pageId: "design-package-planner",
      title: `${
        projectDetails?.name || "Project"
      } - Design Packages Planner - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
        `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
      )}`,
      query: filters.q,
      initialState: {},
      context: {},
      skipColumnIds: [],
      headerHeight: undefined,
      rowHeight: undefined
    };
    savePrintViewState(printViewState);
    window.open(`/project/${projectId}/printView`, "_blank");
  };

  const addPackage = () => {
    if (canAdd) onAddDesignPackage?.();
  };

  const body = () => {
    if (loading && projectIsEmpty)
      return (
        <div className="flex justify-center py-10">
          <Spin size="large" />
        </div>
      );
    if (error)
      return (
        <Result
          status="error"
          title="Couldn't load the planner"
          extra={<Button onClick={() => refetch?.()}>Retry</Button>}
        />
      );
    if (missingPackage)
      return (
        <div
          className="flex h-full items-center justify-center px-6 text-center text-sm text-neutral-500"
          data-testid="planner-missing-package"
        >
          Design Package is not found in current page or deleted. Please choose
          any other from the sidebar.
        </div>
      );
    if (projectIsEmpty)
      return (
        <BlankState
          className="h-full"
          title="No Design Packages"
          description="Create a Design Package to define design milestones and plan downstream trade buyout."
          primaryAction={{
            label: "Add New",
            onClick: addPackage,
            disabled: !canAdd,
            disabledTitle: ErrorMessages.PermissionNotGranted,
            "data-testid": "planner-empty-add"
          }}
          data-testid="planner-empty-project"
        />
      );
    if (!selected)
      return (
        <div
          className="flex h-full items-center justify-center text-sm text-neutral-400"
          data-testid="planner-empty-search"
        >
          No Design Packages match your search
        </div>
      );
    return (
      <TimelineView
        projectId={projectId}
        model={focusedModel}
        collapsedIds={new Set()}
        onToggleLane={() => undefined}
        refetchWorkspace={refetch}
        existingTitles={allTitles}
        hideLaneToggle
      />
    );
  };

  return (
    <div className="flex h-full min-h-0" data-testid="planner-card-view">
      <DesignPackageSidebar
        open={sidebarOpen}
        onToggleOpen={() => setSidebarOpen((open) => !open)}
        packages={sidebarPackages}
        totalCount={model.designPackages.length}
        projectIsEmpty={projectIsEmpty}
        selectedId={selected?.id ?? null}
        onSelect={selectPackage}
        query={filters.q}
        onQueryChange={setQuery}
        canAdd={canAdd}
        onAdd={addPackage}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between gap-3 border-b border-ciq-gray-100 px-4 py-4">
          <div className="flex items-center gap-2">
            <CiqIconBox feature="DESIGN_PACKAGE" size={20} padding={8} />
            <div className="text-xl font-semibold text-black">
              Design Packages
            </div>
            <Tag>Beta</Tag>
          </div>
          <div className="flex items-center gap-3">
            <HowToUsePopover />
            {isPrintViewEnabled ? (
              <ExportOptionsPopover
                options={PLANNER_EXPORT_OPTIONS}
                onProceed={() =>
                  ExportPdfPrintModal({
                    content: (
                      <div>
                        <p className="mb-3">
                          This layout is optimized for printing or saving as a
                          PDF.
                        </p>
                        <p className="mb-0">
                          Use your browser&apos;s print settings to adjust
                          scaling, paper size, or orientation. Landscape is
                          recommended for timelines.
                        </p>
                      </div>
                    ),
                    onProceed: onOpenPlannerPrintView
                  })
                }
                excelTableName={projectDetails?.name || "Project"}
                showIncludeComments={false}
                disabled={visibleCount === 0 || !!isProjectArchived}
                buttonType="default"
                buttonTestId="planner-export-button"
              />
            ) : null}
            {addButton}
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-gray-50">
          <PlannerLogProvider value={logValue}>{body()}</PlannerLogProvider>
        </div>
      </div>
    </div>
  );
}
